<template>
  <div class="main-container">
    <el-row>
      <organization-breadcrumb
        :items="[
          $t('plan_payment.title'),
          $t('plan_payment.payment_details.title'),
        ]"
      />
    </el-row>
    <div class="wrapper">
      <div>
        <base-text :title="true">
          {{ $t("plan_payment.payment_details.title") }}
        </base-text>
        <base-text>
          {{ $t("plan_payment.payment_details.desc") }}
        </base-text>
        <div class="plan-details" v-if="paymentMethod">
          <el-row type="flex" justify="space-between">
            <el-col :span="12" class="payment-method-number">
              {{
                $t("plan_payment.payment_details.ending_in", {
                  method: paymentMethod.brand,
                  number: paymentMethod.last4,
                })
              }}
            </el-col>
            <el-col :span="12" class="payment-method-number align-right">
              {{
                $t("plan_payment.payment_details.expiry_date", {
                  month: paymentMethod.exp_month,
                  year: paymentMethod.exp_year,
                })
              }}
            </el-col>
          </el-row>
          <el-divider></el-divider>
          <el-row :gutter="20" type="flex" align="middle" class="mt-px-12">
            <el-col :span="6" v-if="subscriptionId">
              <el-link
                type="primary"
                :underline="false"
                @click="addCardVisible = true"
              >
                {{ $t("plan_payment.payment_details.add_card") }}
              </el-link>
            </el-col>
            <el-col :span="6" v-show="false">
              <el-link
                type="danger"
                :underline="false"
                @click="confirmDialogVisible = true"
              >
                {{ $t("plan_payment.payment_details.remove_card") }}
              </el-link>
            </el-col>
          </el-row>
        </div>
      </div>
      <div class="new-card" v-if="addCardVisible">
        <base-text :title="true">
          {{ $t("plan_payment.payment_details.add_new_card") }}
        </base-text>
        <div class="stripe">
          <el-row>
            <base-text>
              {{ $t("plan_payment.payment_method.card_details") }}
            </base-text>
          </el-row>
          <el-row class="mb-px-32">
            <stripe-element-card
              ref="elementRef"
              :pk="publishableKey"
              :hide-postal-code="true"
              @token="stripeTokenCreated"
              @error="stripeError"
            />
          </el-row>
          <el-row :gutter="56" class="mt-px-16">
            <el-col :span="6">
              <tertiary-button @click.native="handleCancelNewCard">
                {{ $t("plan_payment.payment_details.cancel") }}
              </tertiary-button>
            </el-col>
            <el-col :span="12">
              <primary-button
                :loading="loading"
                @click.native="handleSaveNewCard"
                class="bigger"
              >
                {{ $t("plan_payment.payment_details.save_new_card") }}
              </primary-button>
            </el-col>
          </el-row>
        </div>
      </div>
    </div>
    <el-dialog :visible.sync="confirmDialogVisible" width="30%" center>
      <span class="dialog-title">
        {{ $t("plan_payment.payment_details.save_success_message") }}
      </span>
      <span slot="footer" class="dialog-footer">
        <el-button @click="handleReloadWithNewCard" round>
          {{ $t("plan_payment.payment_details.close_text") }}
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import OrganizationBreadcrumb from "@/components/common/OrganizationBreadcrumb";
import PrimaryButton from "@/components/common/buttons/PrimaryButton";
import TertiaryButton from "@/components/common/buttons/TertiaryButton";
import BaseText from "@/components/common/BaseText.vue";
import axios from "@/utils/axios";
import { StripeElementCard } from "@vue-stripe/vue-stripe";
import { warningMessage } from "@/utils/messages";

export default {
  components: {
    OrganizationBreadcrumb,
    PrimaryButton,
    StripeElementCard,
    TertiaryButton,
    BaseText,
  },
  data() {
    return {
      payment: undefined,
      confirmDialogVisible: false,
      addCardVisible: false,
      loading: false,
      subscriptionId: undefined,
      paymentMethod: undefined,
      publishableKey: process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY,
      paymentMethodNew: undefined,
    };
  },
  computed: {
    organizationId() {
      return this.$store.state.organization.currentOrganizationId;
    },
    organizationData() {
      return this.$store.state.organization.currentOrganization;
    },
    headers() {
      return {
        store: "stripe",
        "x-session-token": localStorage.getItem("payment-token"),
        "x-org-token": Buffer.from(
          `${this.organizationId} ${Date.now()}`
        ).toString("base64"),
      };
    },
  },
  async mounted() {
    this.loading = true;
    const organizationId = this.organizationId;
    const paymentId = Number(this.$route.params.paymentId);
    if (!this.organizationData) {
      await this.$store.dispatch("organization/getCurrentOrganization", {
        organizationId,
      });
    }
    await this.getPayment(paymentId);
  },
  methods: {
    async getPayment(paymentId) {
      let url = `v1/checkouts/${this.organizationId}/payments`;
      const response = await axios.get(url);
      this.payment = response.data.find((item) => item.id === paymentId);
      await this.getSubscriptionId(this.payment.id);
    },
    async getSubscriptionId(orderId) {
      try {
        const { data } = await this.$axios.get(
          process.env.VUE_APP_PAYMENT_URL + "/organization/subscriptions",
          { headers: this.headers }
        );
        const subscription = data.find((item) => item.order_id == orderId);
        if (subscription) {
          this.subscriptionId = subscription.id;
          this.paymentMethod = subscription.payment_method;
        }
      } catch (e) {
        this.handleError(e);
      } finally {
        this.loading = false;
      }
    },
    handleError(e) {
      if (e.response) {
        const { detail } = e.response.data;
        warningMessage(detail, "elsa-message warning", {
          showClose: false,
          duration: 5000,
        })(this);
      }
    },
    async stripeTokenCreated(token) {
      this.loading = true;
      try {
        const { data } = await this.$axios.put(
          process.env.VUE_APP_PAYMENT_URL +
            `/organization/subscription/${this.subscriptionId}/payment_method`,
          { token: token.id },
          { headers: this.headers }
        );
        this.confirmDialogVisible = true;
        this.paymentMethodNew = data.payment_method;
      } catch (e) {
        this.handleError(e);
      } finally {
        this.loading = false;
      }
    },
    stripeError(err) {
      err;
    },
    handleCancelNewCard() {
      this.$router.push({
        name: "OrganizationPlan",
        params: { organizationId: this.organizationId },
      });
    },
    handleSaveNewCard() {
      this.$refs.elementRef.submit();
    },
    handleReloadWithNewCard() {
      this.$refs.elementRef.clear();
      this.paymentMethod = this.paymentMethodNew;
      this.confirmDialogVisible = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.plan-details {
  border: 1px solid #e6e6e6;
  border-radius: 12px;
  margin-top: 42px;
  padding: 24px;
  max-width: 585px;
}

.payment-method-number {
  font-size: 16px;
  line-height: 24px;

  &:first-letter {
    text-transform: uppercase;
  }
}

.align-right {
  text-align: right;
}

.new-card {
  margin-top: 63px;

  .stripe {
    max-width: 585px;
  }
}

::v-deep {
  #stripe-element-form {
    #stripe-element-mount-point {
      border: 1px solid rgba(0, 0, 0, 0.2);
      box-sizing: border-box;
      border-radius: 55px;
      box-shadow: unset !important;
    }
    #stripe-element-errors {
      color: red;
      padding-top: 12px;
    }
  }

  .el-dialog__body {
    text-align: center !important;
    padding: 25px !important;
  }

  .dialog-title {
    white-space: pre-line;
    font-weight: 600;
    font-size: 16px;
    line-height: 24px;
  }

  .el-button {
    font-weight: 600;
    font-size: 16px;
    line-height: 24px;
    color: #478dff;
    border: 1px solid #478dff;
  }
}
</style>
